import type {
  ModuleStructure,
  StructureCacheByModule,
  StructureOrigin,
} from './index.h'

import { createEffect, sample } from 'effector'
import { appearanceFeature } from '~/features/appearance'
import {
  $structure,
  $structureOrigin,
  applyModuleStructure,
  initModule,
  leaveModule,
  resetStructure,
  setStructure,
} from './model'

const $identifier = appearanceFeature.$currentIdentifier

const cache: StructureCacheByModule = {}

const writeCacheFx = createEffect<
  {
    components: ModuleStructure | null
    structureOrigin: StructureOrigin
    identifier: string
  },
  void
>(({ components, structureOrigin, identifier }) => {
  cache[identifier] = { structure: components, structureOrigin }
})

const clearCacheFx = createEffect<void, void>(() => {
  for (const key in cache) delete cache[key]
})

/**
 * Apply `patch` to every cached module structure.
 *
 * Only the module the user is on lives in `$structure`; the rows of the modules
 * behind it stay here until they are restored. A change made on one page - such
 * as add/remove from My List on a details page - has to reach them here, or the
 * stale rows come back on navigation.
 */
export const patchCachedStructures = (
  patch: (structure: ModuleStructure) => ModuleStructure
): void => {
  for (const identifier in cache) {
    const cached = cache[identifier]
    if (!cached?.structure) continue

    const structure = patch(cached.structure)
    if (structure !== cached.structure) {
      cache[identifier] = { ...cached, structure }
    }
  }
}

// restore cached data
sample({
  clock: initModule,
  fn: ({
    identifier,
  }): {
    identifier: string
    structure: ModuleStructure | null
    structureOrigin: StructureOrigin
  } => {
    const cached = cache[identifier]

    // clear previous module data
    if (!cached) {
      return {
        identifier,
        structure: null,
        structureOrigin: 'backend',
      }
    }

    return {
      identifier,
      structure: cached.structure,
      structureOrigin: cached.structureOrigin,
    }
  },
  target: setStructure,
})

// Persist only from atomic events that carry the correct module identifier
sample({
  clock: setStructure,
  fn: ({ identifier, structure, structureOrigin }) => ({
    components: structure,
    structureOrigin: structureOrigin ?? 'backend',
    identifier,
  }),
  target: writeCacheFx,
})

sample({
  clock: applyModuleStructure,
  source: $identifier,
  filter: Boolean,
  fn: (identifier, { structure, structureOrigin }) => ({
    components: structure,
    structureOrigin,
    identifier: identifier!,
  }),
  target: writeCacheFx,
})

// Save on leave only while this module is still current — avoids overwriting
// another module's cache when leave races after init of the next module.
sample({
  clock: leaveModule,
  source: {
    components: $structure,
    structureOrigin: $structureOrigin,
    currentId: $identifier,
  },
  filter: ({ currentId }, { identifier }) =>
    Boolean(identifier) && currentId === identifier,
  fn: ({ components, structureOrigin }, { identifier }) => ({
    components,
    structureOrigin,
    identifier,
  }),
  target: writeCacheFx,
})

// clear modules data
sample({
  clock: resetStructure,
  target: clearCacheFx,
})
